/**
 * Browser Compatibility Polyfills
 * 
 * Provides fallbacks for modern APIs that aren't supported in older browsers.
 * Ensures iWishBag works across a wide range of browsers for international users.
 */

/**
 * Polyfill for crypto.randomUUID() - Safari < 15.4, Chrome < 92, Firefox < 95
 * Critical for quote generation system
 */
export function setupCryptoPolyfill() {
  if (typeof crypto !== 'undefined' && !crypto.randomUUID) {
    console.log('[BrowserPolyfills] Adding crypto.randomUUID polyfill for browser compatibility');
    
    crypto.randomUUID = function(): string {
      // Use crypto.getRandomValues if available (most modern browsers)
      if (crypto.getRandomValues) {
        const array = new Uint8Array(16);
        crypto.getRandomValues(array);
        
        // Convert to UUID format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
        const hex = Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
        return [
          hex.slice(0, 8),
          hex.slice(8, 12),
          '4' + hex.slice(13, 16), // Version 4 UUID
          ((parseInt(hex[16], 16) & 0x3) | 0x8).toString(16) + hex.slice(17, 20), // Variant bits
          hex.slice(20, 32)
        ].join('-');
      } 
      
      // Fallback for very old browsers - less cryptographically secure but functional
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    };
  }
}

/**
 * Polyfill for AbortSignal.timeout() - Safari < 16.0, Firefox < 103
 * Critical for network request timeouts in currency/location services
 */
export function setupAbortSignalPolyfill() {
  if (typeof AbortSignal !== 'undefined' && !AbortSignal.timeout) {
    console.log('[BrowserPolyfills] Adding AbortSignal.timeout polyfill for browser compatibility');
    
    AbortSignal.timeout = function(milliseconds: number): AbortSignal {
      const controller = new AbortController();
      
      setTimeout(() => {
        controller.abort();
      }, milliseconds);
      
      return controller.signal;
    };
  }
}

/**
 * Polyfill for Intl.DateTimeFormat timezone detection
 * Fallback for CloudFlare country detection system
 */
export function getTimezoneWithFallback(): string {
  try {
    // Modern browser support
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.warn('[BrowserPolyfills] Timezone detection failed, using fallback');
    
    // Fallback: Use timezone offset to guess region
    const offset = new Date().getTimezoneOffset();
    
    // Common timezone guesses based on offset (in minutes)
    const timezoneGuesses: Record<number, string> = {
      '-345': 'Asia/Kathmandu',     // Nepal: UTC+5:45
      '-330': 'Asia/Kolkata',       // India: UTC+5:30
      '0': 'Europe/London',         // UK: UTC+0
      '-480': 'Asia/Shanghai',      // China: UTC+8
      '300': 'America/New_York',    // US East: UTC-5
      '480': 'America/Los_Angeles', // US West: UTC-8
    };
    
    return timezoneGuesses[offset] || 'UTC';
  }
}

/**
 * Enhanced fetch with timeout for older browsers
 * Provides consistent timeout behavior across all browsers
 */
export function fetchWithTimeout(url: string, options: RequestInit & { timeout?: number } = {}): Promise<Response> {
  const { timeout = 5000, ...fetchOptions } = options;
  
  // If AbortSignal.timeout is available (after polyfill), use it
  if (AbortSignal.timeout) {
    return fetch(url, {
      ...fetchOptions,
      signal: AbortSignal.timeout(timeout)
    });
  }
  
  // Fallback: Manual timeout with AbortController
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  return fetch(url, {
    ...fetchOptions,
    signal: controller.signal
  }).finally(() => {
    clearTimeout(timeoutId);
  });
}

/**
 * Initialize all polyfills
 * Call this early in your application startup
 */
export function initializeBrowserPolyfills() {
  console.log('[BrowserPolyfills] Initializing browser compatibility polyfills...');
  
  setupCryptoPolyfill();
  setupAbortSignalPolyfill();
  
  // Note: requestIdleCallback polyfill is already in assetPreloader.ts
  
  console.log('[BrowserPolyfills] ✅ Browser compatibility polyfills initialized');
}

// Browser support detection utilities
export const browserSupport = {
  cryptoRandomUUID: typeof crypto !== 'undefined' && !!crypto.randomUUID,
  abortSignalTimeout: typeof AbortSignal !== 'undefined' && !!AbortSignal.timeout,
  requestIdleCallback: typeof requestIdleCallback !== 'undefined',
  intlTimeZone: (() => {
    try {
      return !!Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return false;
    }
  })(),
  
  // Overall compatibility score (0-1)
  get compatibilityScore(): number {
    const features = [
      this.cryptoRandomUUID,
      this.abortSignalTimeout, 
      this.requestIdleCallback,
      this.intlTimeZone
    ];
    return features.filter(Boolean).length / features.length;
  }
};